import { getSortedTablePage } from "@kontextwerk/web-sdk/core/layout/tableSort"
import type { ColumnDef, DataRow } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"

export const STABLE_ROW_NUMBER_COLUMN_ID = "rowNumber"

export type StableRowNumberState = {
  nextNumber: number
  byIdentity: Record<string, number>
}

export type StableNumberedRow = {
  [STABLE_ROW_NUMBER_COLUMN_ID]: number
}

export type StableTableSortState = {
  columnId: string
  key: string
  dir: "asc" | "desc"
} | null

export const createStableRowNumberState = (): StableRowNumberState => ({
  nextNumber: 1,
  byIdentity: {},
})

const stableStringValue = (value: unknown): string => {
  if (typeof value === "string" && value.trim()) return value.trim()

  if (typeof value === "number" && Number.isFinite(value)) return String(value)

  return ""
}

export const stableRowNumberIdentity = (row: DataRow, index: number): string =>
  stableStringValue(row.id) || `row:${index}`

export const withStableRowNumbers = <Row extends DataRow>(
  rows: Row[],
  state: StableRowNumberState,
  getIdentity: (row: Row, index: number) => string = stableRowNumberIdentity,
): Array<Row & StableNumberedRow> => {
  const nextByIdentity = { ...state.byIdentity }
  let nextNumber = state.nextNumber

  const numberedRows = rows.map((row, index) => {
    const identity = getIdentity(row, index) || `row:${index}`
    const rowNumber = nextByIdentity[identity] ?? nextNumber++
    nextByIdentity[identity] = rowNumber

    return {
      ...row,
      [STABLE_ROW_NUMBER_COLUMN_ID]: rowNumber,
    }
  })

  state.byIdentity = nextByIdentity
  state.nextNumber = nextNumber

  return numberedRows
}

export const withoutStableRowNumbers = <Row extends DataRow>(rows: Row[]): Row[] =>
  rows.map((row) => {
    const { [STABLE_ROW_NUMBER_COLUMN_ID]: _rowNumber, ...rest } = row

    return rest as Row
  })

export const stableRowNumberColumn = (label = "Nr"): ColumnDef => ({
  id: STABLE_ROW_NUMBER_COLUMN_ID,
  label,
  sortable: true,
  sortType: "number",
  defaultSortDir: "asc",
  getSortValue: (row) => row[STABLE_ROW_NUMBER_COLUMN_ID],
})

export const stableRowNumberSort: NonNullable<StableTableSortState> = {
  columnId: STABLE_ROW_NUMBER_COLUMN_ID,
  key: STABLE_ROW_NUMBER_COLUMN_ID,
  dir: "asc",
}

export const getStableSortedRowPage = <Row extends DataRow>({
  rows,
  columns,
  sort,
  limit,
  offset,
}: {
  rows: Row[]
  columns: ColumnDef[]
  sort?: StableTableSortState
  limit?: number
  offset?: number
}) =>
  getSortedTablePage({
    rows,
    columns,
    sort: sort ?? stableRowNumberSort,
    limit,
    offset,
  })
